import React from 'react';
import { Card as CardComponent } from 'theme-ui';
import { Layout, Stack, Main } from '@layout';
import CardList from '@components/CardList';
import Card from '@components/Card';
import Divider from '@components/Divider';
import Seo from '@widgets/Seo';
import TableOfContentsExpanded from '@widgets/TableOfContentsExpanded';
import {
  PostImage,
  PostBody,
  PostComments,
  PostCommentsFacebook,
  PostCommentsGraph,
  PostTagsShare,
} from '@widgets/Post';
import CookieBanner from '../../flow-ui-widgets/CookieBanner/CookieBanner';

const getMetaData = (slug) => {
  switch (slug) {
    case '/blog/how-can-security-guards-improve-the-safety-of-a-community/':
      return {
        title: 'Tips for Security Guards to Enhance Safety',
        description:
          'How can security guards improve community safety? Discover best practices for effective patrolling, communication, using modern tech, and emergency readiness.',
      };
    case '/blog/what-are-the-common-causes-of-accidents-at-construction-sites/':
      return {
        title: 'The common causes of accidents at construction sites',
        description:
          'Learn about the common causes of accidents at construction sites and the steps you can take to prevent them, ensuring a safer work environment.',
      };
    case '/blog/streamline-building-inspections-and-maintenance-with-targpatrol/':
      return {
        title: 'Streamline building inspections using TARGPatrol App',
        description:
          'Efficiently manage building inspections and maintenance tasks with TARGPatrol. Streamline your workflow, track progress, and resolve incidents with ease.',
      };
    case '/blog/how-can-rfid-tags-improve-the-cleaning-and-maintainance-industry/': {
      return {
        title: 'How to use RFID tags to improve the cleaning services',
        description:
          'How do RFID tags enhance the cleaning business? Discover how to use RFID to streamline tracking, reduce costs, and ensure high-quality service.',
      };
    }
    case '/blog/improve-quality-standards-in-your-chemical-processing-manufacture/':
      return {
        title: 'Boost quality in chemical processing manufacturing',
        description:
          'Learn how to enhance quality standards in your chemical production with TARGPatrol, an innovative process control tool designed to ensure compliance.',
      };
    case '/blog/revolutionize-workplace-safety-and-quality-management-with-targpatrol/':
      return {
        title: 'TARGPatrol for Workplace Safety and Quality Management',
        description:
          'Discover how to simplify inspections, enable mobile data collection, and access advanced reporting and analytics with our task and project management software!',
      };
    case '/blog/exploring-nfc-technology-enhancing-business-efficiency-and-connectivity/':
      return {
        title: 'How NFC technology streamlines workflows',
        description:
          'Discover the untapped potential of NFC technology, exploring its applications, benefits, and how it can revolutionize various industries.',
      };
    case '/blog/maintenance-activities-in-chemical-processing-plants-can-be-complicated.../':
      return {
        title: 'Best practices for chemical industry maintenance',
        description:
          'Maintenance in chemical processing plants can be complex. Discover effective strategies to streamline operations, enhance safety, and ensure efficiency.',
      };
    case '/blog/quality-improvement-a-key-to-success-in-facility-management-with-targpatrol/':
      return {
        title: 'Improving Facility Management with task tracking',
        description:
          'TARGPatrol enhances quality management by tracking task execution, generating reports, and providing analytics to reduce costs and improve efficiency.',
      };
    case '/blog/solving-problems-swiftly-how-targpatrols-issue-management-streamlines-operations/':
      return {
        title: "Swift Problem Solving with TARGPatrol's Issue Management",
        description:
          'Streamline issue tracking, prioritize tasks, and ensure timely resolutions. Enhance efficiency and maintain high service standards with our task tracking tools.',
      };
    case '/blog/the-significance-of-task-management-software-boosting-productivity-and-efficiency/':
      return {
        title: 'Why Your Business Needs Task Management Software',
        description:
          'Optimize project success with Task Management Software. Plan, track progress, and collaborate in real time using an intuitive cloud-based platform.',
      };
    case '/blog/how-to-create-a-cloud-service-for-managing-and-controling-detour-routes-at-enterprises/':
      return {
        title: 'How to create a cloud service for managing patrols?',
        description:
          'Discover how to create a cloud platform for efficient tour and patrol management. Learn the best practices and keyfeatures for successful implementation.',
      };
    case '/blog/how-can-rfid-and-nfc-improve-safety-and-production-quality-in-the-manufacturing-industry/':
      return {
        title: 'How can RFID and NFC improve manufacturing industry?',
        description:
          'Enhance safety and quality in manufacturing with RFID and NFC technology. Leverage task management software to maximize benefits and streamline operations.',
      };
    case '/blog/how-can-the-aviation-industry-use-rfid-and-nfc-tech-to-streamline-maintainance-and-repair/':
      return {
        title: 'How can the aviation industry use RFID and NFC tech?',
        description:
          'Aviation leverages RFID and NFC to optimize maintenance, reduce downtime, and ensure safety through efficient repair management.',
      };
    case '/blog/how-can-you-improve-quality-of-care-in-healthcare-institutions-using-rfid-and-nfc-technology/':
      return {
        title: 'Why healthcare institutions need RFID and NFC technology',
        description:
          'Improve healthcare safety and efficiency with RFID and NFC technology. Optimize patient tracking, staff workflows, and equipment management for better care.',
      };
  }
};

const Post = ({
  data: { post, tagCategoryPosts, tagPosts, categoryPosts, previous, next },
  location,
  ...props
}) => {
  const relatedPosts = [
    ...(tagCategoryPosts ? tagCategoryPosts.nodes : []),
    ...(tagPosts ? tagPosts.nodes : []),
    ...(categoryPosts ? categoryPosts.nodes : []),
  ];
  const { pageContext: { services = {}, siteUrl } = {} } = props;
  const metadata = getMetaData(post.slug);

  return (
    <Layout {...props}>
      <Seo
        {...post}
        siteUrl={siteUrl}
        postUrl={location.href}
        title={metadata?.title || post.title}
        description={metadata?.description}
      />
      <CookieBanner />
      <Divider />
      <Stack effectProps={{ effect: 'fadeInDown' }}>
        <Main>
          <Card {...post} variant='horizontal-hero' omitExcerpt omitMedia />
        </Main>
      </Stack>
      <Divider space={3} />
      <Stack effectProps={{ fraction: 0 }}>
        <Main>
          {post.tableOfContents?.items && (
            <>
              <TableOfContentsExpanded {...post} />
              <Divider />
            </>
          )}
          <CardComponent variant='paper-lg'>
            <PostImage {...post} inCard />
            <PostBody {...post} />
            <PostTagsShare {...post} location={props.location} />
            {services.disqus && <PostComments {...post} />}
            {services.graphComment && <PostCommentsGraph {...post} />}
            {services.facebookComment && (
              <PostCommentsFacebook {...post} siteUrl={siteUrl} />
            )}
          </CardComponent>
          <Divider />
          {post.category && (
            <CardList
              title={
                <dev
                  sx={{
                    boxSizing: 'border-box',
                    margin: 0,
                    minWidth: 0,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 'bold',
                    lineHeight: 1.33,
                    display: 'block',
                    color: 'var(--theme-ui-colors-heading, #101828)',
                    textDecoration: 'none',
                    fontSize: '1.5rem',
                    position: 'relative',
                    paddingLeft: 0,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: 0,
                  }}
                >
                  Related Posts
                </dev>
              }
              nodes={relatedPosts}
              variant={['horizontal-aside']}
              columns={[1, 2, 2, 2]}
              limit={6}
              distinct
              omitMedia
              omitCategory
            />
          )}
          <Divider />
        </Main>
      </Stack>
    </Layout>
  );
};

export default Post;
